<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../../framework/js/api.js';
  import { auth } from '../../framework/js/auth.js';
  import { toast } from '../../framework/js/toast.js';
  import { push } from 'svelte-spa-router';
  import PackPurchaseModal from '../../framework/components/PackPurchaseModal.svelte';
  import AddonPurchaseModal from '../../framework/components/AddonPurchaseModal.svelte';
  import AddCardModal from '../../framework/components/AddCardModal.svelte';
  import ConfirmDialog from '../ConfirmDialog.svelte';
  import { AppPageHeader, StatusBadge, TabBar } from '../../framework/components/app';

  export let invoiceUuid: string = ''; // passed by Dashboard, unused here

  let teamData: any = null;
  let usage: any = null;
  let recentInvoices: any[] = [];
  let savedCards: any[] = [];
  let showPackModal = false;
  let showAddonModal = false;
  let addonServices: any[] = [];
  let showAddCardModal = false;
  let showRemoveConfirm = false;
  let cardToRemove: { id: string; brand: string; last4: string } | null = null;
  let showSetDefaultConfirm = false;
  let cardToSetDefault: { id: string } | null = null;
  let isSettingDefault = false;
  let isRemovingCard = false;
  let isTeamOwner = false;
  let billingTab: 'invoices' | 'payment-methods' = 'invoices';

  const billingTabs = [
    { value: 'invoices', label: 'Invoices', icon: 'file alternate outline' },
    { value: 'payment-methods', label: 'Payment methods', icon: 'credit card outline' },
  ];

  // Individual loading states for each section
  let isLoadingPlan = true;
  let isLoadingUsage = true;
  let isLoadingInvoices = true;
  let isLoadingCards = true;

  // Track if data has been loaded (to distinguish between "loading" and "no data")
  let planLoaded = false;
  let usageLoaded = false;
  let invoicesLoaded = false;
  let cardsLoaded = false;

  // Free tier defaults when no plan
  const FREE_TIER = {
    name: 'Free Tier',
    price: 0,
    included_briefs: 0,
  };

  onMount(() => {
    // Load all sections in parallel, each updating independently
    loadPlan();
    loadUsage();
    loadInvoices();
    loadCards();
    loadAddons();
  });

  async function loadPlan() {
    isLoadingPlan = true;
    try {
      const teamUuid = $auth.user?.active_team_uuid;
      if (teamUuid) {
        const [teamResponse, membersResponse] = await Promise.all([
          api.getTeam(teamUuid) as Promise<any>,
          api.getTeamMembers(teamUuid) as Promise<any>,
        ]);
        if (teamResponse?.success && teamResponse.data?.team) {
          teamData = teamResponse.data.team;
        }
        if (membersResponse?.success) {
          const members = membersResponse.data?.members || [];
          const currentUserMember = members.find((m: any) => m.user_uuid === $auth.user?.uuid);
          isTeamOwner = currentUserMember?.role === 'owner';
        }
      }
    } catch (error) {
      console.error('Error loading plan:', error);
    } finally {
      isLoadingPlan = false;
      planLoaded = true;
    }
  }

  async function loadAddons() {
    try {
      const response = (await api.getAddonServices()) as any;
      if (response?.success) {
        addonServices = response.data?.products || [];
      }
    } catch (error) {
      console.error('Error loading add-ons:', error);
    }
  }

  function handleAddonPurchased() {
    void loadAddons();
    reloadAll();
  }

  async function loadUsage() {
    isLoadingUsage = true;
    try {
      const teamUuid = $auth.user?.active_team_uuid;
      if (teamUuid) {
        const teamUsageResponse = await api.getTeamUsage(teamUuid) as any;
        if (teamUsageResponse?.success) {
          usage = teamUsageResponse.data || null;
        }
      }
    } catch (error) {
      console.error('Error loading usage:', error);
    } finally {
      isLoadingUsage = false;
      usageLoaded = true;
    }
  }

  async function loadInvoices() {
    isLoadingInvoices = true;
    try {
      const response = await api.getUserInvoices($auth.user.uuid) as any;
      if (response && response.success) {
        recentInvoices = (response.data?.invoices || []).slice(0, 5);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      isLoadingInvoices = false;
      invoicesLoaded = true;
    }
  }

  async function loadCards() {
    isLoadingCards = true;
    try {
      const response = await api.getUserCards($auth.user.uuid) as any;
      if (response && response.success) {
        savedCards = response.data || [];
      }
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      isLoadingCards = false;
      cardsLoaded = true;
    }
  }
  
  async function reloadAll() {
    loadPlan();
    loadUsage();
    loadInvoices();
    loadCards();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('billing-updated'));
    }
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(navigator.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function getCardBrandIcon(brand: string): string {
    const brandIcons: Record<string, string> = {
      'visa': 'cc visa',
      'mastercard': 'cc mastercard',
      'amex': 'cc amex',
      'discover': 'cc discover',
      'diners': 'cc diners club',
      'jcb': 'cc jcb',
    };
    return brandIcons[brand?.toLowerCase()] || 'credit card';
  }

  function openSetDefaultConfirm(card: { id: string }) {
    cardToSetDefault = card;
    showSetDefaultConfirm = true;
  }

  function closeSetDefaultConfirm() {
    showSetDefaultConfirm = false;
    cardToSetDefault = null;
  }

  async function doSetDefaultCard() {
    if (!cardToSetDefault) return;
    isSettingDefault = true;
    try {
      const response = await api.setDefaultCard($auth.user.uuid, cardToSetDefault.id) as any;
      if (response?.success) {
        toast.showNotification(true, 'Default payment method updated.');
        await loadCards();
      } else {
        toast.showNotification(false, response?.message || 'Could not set default card.');
      }
    } catch (e) {
      toast.showNotification(false, 'Could not set default card.');
    } finally {
      isSettingDefault = false;
      closeSetDefaultConfirm();
    }
  }

  function openRemoveConfirm(card: { id: string; brand: string; last4: string }) {
    cardToRemove = card;
    showRemoveConfirm = true;
  }

  function closeRemoveConfirm() {
    showRemoveConfirm = false;
    cardToRemove = null;
  }

  async function doRemoveCard() {
    if (!cardToRemove) return;
    isRemovingCard = true;
    try {
      const response = await api.removeCard($auth.user.uuid, cardToRemove.id) as any;
      if (response?.success) {
        toast.showNotification(true, 'Card removed.');
        await loadCards();
      } else {
        toast.showNotification(false, response?.message || 'Could not remove card.');
      }
    } catch (e) {
      toast.showNotification(false, 'Could not remove card.');
    } finally {
      isRemovingCard = false;
      closeRemoveConfirm();
    }
  }

  function capitalizeFirst(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Reactive values for display - team-based billing only
  $: teamPlan = teamData?.payment_plan || null;
  $: isOnTrial = teamData?.is_on_trial ?? false;
  $: trialDaysRemaining = teamData?.trial_days_remaining ?? null;
  $: trialEndsAt = teamData?.trial_ends_at ?? null;
  $: planName = teamPlan?.name || FREE_TIER.name;
  $: planPrice = teamPlan?.price ?? FREE_TIER.price;
  $: planBriefsIncluded = usage?.plan_credits_included ?? usage?.plan_briefs_included ?? teamPlan?.included_briefs ?? FREE_TIER.included_briefs;
  $: planBriefsUsed = usage?.plan_credits_used ?? usage?.plan_briefs_used ?? 0;
  $: planBriefsRemaining = usage?.plan_credits_remaining ?? usage?.plan_briefs_remaining ?? 0;
  $: renewalDateLabel = usage?.renewal_date
    ? new Date(usage.renewal_date).toLocaleDateString(navigator.language, { dateStyle: 'medium' })
    : null;
  $: usagePercentage = planBriefsIncluded > 0 ? Math.min(100, (planBriefsUsed / planBriefsIncluded) * 100) : 0;
  $: isFreeTier = !teamPlan;
</script>

<div class="payment-plan-page" data-invoice-context={invoiceUuid || undefined}>
  <AppPageHeader
    title="My Plan"
    subtitle="Manage your subscription, usage, and billing."
    showAction={false}
  />
  
  <div class="info-grid">
    <!-- Current Plan + Monthly Usage -->
    <div class="info-card plan-card-combined">
      <h3 class="card-title">Current Plan</h3>
      {#if isLoadingPlan || isLoadingUsage}
        <div class="skeleton-content">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-price"></div>
          <div class="skeleton skeleton-badge"></div>
          <div class="skeleton skeleton-progress"></div>
          <div class="skeleton skeleton-text"></div>
          <div class="skeleton skeleton-button"></div>
        </div>
      {:else}
        <div class="plan-info">
          <div class="plan-name-large">{planName}</div>
          <div class="plan-price-large">
            {formatPrice(planPrice)}<span class="price-period">/month</span>
          </div>
          {#if isOnTrial}
            <StatusBadge label="Free trial" tone="warn" />
            <div class="trial-info">
              <div class="trial-expiry">
                <i class="clock outline icon" aria-hidden="true"></i>
                <span>{trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining</span>
              </div>
              {#if trialEndsAt}
                <div class="trial-expiry-date">
                  Trial ends {formatDate(trialEndsAt)}
                </div>
              {/if}
            </div>
          {:else if teamPlan}
            <StatusBadge label="Active" tone="success" />
          {:else}
            <StatusBadge label="No active subscription" tone="neutral" />
          {/if}

          <div class="plan-usage-block">
            <h4 class="plan-usage-label">Monthly usage</h4>
            {#if isOnTrial}
              <div class="usage-info">
                <div class="trial-usage-message">
                  <i class="gift icon" aria-hidden="true"></i>
                  <span>Unlimited AI outputs during your free trial</span>
                </div>
                {#if planBriefsUsed > 0}
                  <div class="usage-remaining">
                    {planBriefsUsed} {planBriefsUsed === 1 ? 'output' : 'outputs'} created so far
                  </div>
                {/if}
              </div>
            {:else if isFreeTier}
              <div class="usage-info">
                <div class="free-tier-message">
                  <i class="info circle icon" aria-hidden="true"></i>
                  <span>Subscribe to a plan to generate AI outputs</span>
                </div>
              </div>
            {:else}
              <div class="usage-info">
                <div class="usage-main">
                  <span class="usage-current">{planBriefsUsed}</span>
                  <span class="usage-separator">/</span>
                  <span class="usage-total">{planBriefsIncluded}</span>
                  <span class="usage-label-text">credits used</span>
                </div>
                <div class="usage-progress">
                  <div
                    class="usage-progress-bar"
                    class:warning={usagePercentage >= 80 && usagePercentage < 100}
                    class:full={usagePercentage >= 100}
                    style="width: {usagePercentage}%"
                  ></div>
                </div>
                <div class="usage-remaining">
                  {planBriefsRemaining} credits remaining this billing period
                  {#if renewalDateLabel}
                    <span class="renewal-hint">(resets {renewalDateLabel})</span>
                  {/if}
                </div>
              </div>
            {/if}
          </div>

          {#if isTeamOwner}
            <button class="change-plan-button" on:click={() => push('/payment-plans')}>
              {isFreeTier ? 'Upgrade Plan' : 'Change Plan'}
            </button>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Extra Credits Card -->
    <div class="info-card">
      <h3 class="card-title">Extra Credits (Packs)</h3>
      {#if isLoadingUsage}
        <div class="skeleton-content">
          <div class="skeleton skeleton-usage"></div>
          <div class="skeleton skeleton-text"></div>
          <div class="skeleton skeleton-button"></div>
        </div>
      {:else}
        <div class="credits-info">
          <div class="credits-main">
            <span class="credits-available">{usage?.pack_credits_remaining ?? usage?.pack_briefs_remaining ?? 0}</span>
            <span class="credits-label">extra credits available</span>
          </div>
          {#if usage?.packs && usage.packs.length > 0}
            <div class="packs-summary">
              {#each usage.packs as pack}
                <div class="pack-summary-item">
                  <span class="pack-name">{pack.name}</span>
                  <span class="pack-briefs">{pack.credits_remaining ?? pack.briefs_remaining} remaining</span>
                </div>
              {/each}
            </div>
          {:else}
            <div class="no-packs">No active packs</div>
          {/if}
          {#if isTeamOwner}
            <button class="purchase-pack-button" on:click={() => showPackModal = true}>
              Purchase Pack
            </button>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Add-on Services -->
    <div class="info-card">
      <h3 class="card-title">Add-on Services</h3>
      {#if addonServices.length === 0 && (!usage?.addon_purchases || usage.addon_purchases.length === 0)}
        <p class="addon-empty">No add-on services are listed yet. Check back soon for recruitment, consultation, and other services.</p>
      {:else}
        {#if usage?.addon_purchases?.length}
          <div class="addon-purchases">
            <h4 class="addon-subtitle">Recent purchases</h4>
            {#each usage.addon_purchases as purchase}
              <div class="addon-purchase-row">
                <span>{purchase.product_name}</span>
                <span class="addon-purchase-meta">
                  {new Date(purchase.purchased_date).toLocaleDateString(navigator.language, { dateStyle: 'medium' })}
                </span>
              </div>
            {/each}
          </div>
        {/if}
        {#if isTeamOwner && addonServices.length > 0}
          <button type="button" class="purchase-pack-button" on:click={() => (showAddonModal = true)}>
            Browse add-ons
          </button>
        {/if}
      {/if}
    </div>
  </div>

    <!-- Billing tabs: invoices + payment methods (owners only) -->
    {#if isTeamOwner}
    <div class="billing-tabs-section">
      <div class="billing-tabs-toolbar">
        <div class="billing-tabs-nav">
          <TabBar tabs={billingTabs} bind:value={billingTab} />
        </div>
        <div class="billing-tab-actions">
          {#if billingTab === 'invoices' && !isLoadingInvoices && recentInvoices.length > 0}
            <button type="button" class="view-all-button" on:click={() => push('/invoices')}>
              View all
            </button>
          {:else if billingTab === 'payment-methods' && !isLoadingCards}
            <button type="button" class="add-card-button" on:click={() => showAddCardModal = true}>
              <i class="plus icon" aria-hidden="true"></i>
              Add card
            </button>
          {/if}
        </div>
      </div>

      {#if billingTab === 'invoices'}
        <div class="billing-tab-panel" role="tabpanel" aria-label="Invoices">
          {#if isLoadingInvoices}
            <div class="invoices-loading">
              <div class="skeleton skeleton-invoice"></div>
              <div class="skeleton skeleton-invoice"></div>
            </div>
          {:else if recentInvoices.length > 0}
            <div class="invoices-list">
              {#each recentInvoices as invoice}
                <div
                  class="invoice-item"
                  on:click={() => {
                    if (typeof window !== 'undefined') window.location.hash = '#/invoices/' + invoice.uuid;
                    else push(`/invoices/${invoice.uuid}`);
                  }}
                  on:keydown={(e) => {
                    if (e.key === 'Enter') {
                      if (typeof window !== 'undefined') window.location.hash = '#/invoices/' + invoice.uuid;
                      else push(`/invoices/${invoice.uuid}`);
                    }
                  }}
                  role="button"
                  tabindex="0"
                >
                  <div class="invoice-main">
                    <div class="invoice-number">#{invoice.invoice_number}</div>
                    <div class="invoice-date">{formatDate(invoice.invoice_date)}</div>
                  </div>
                  <div class="invoice-details">
                    <div class="invoice-amount">{formatPrice(invoice.total)}</div>
                    <div
                      class="invoice-status"
                      class:paid={invoice.is_paid}
                      class:overdue={invoice.status === 'overdue'}
                    >
                      {invoice.is_paid ? 'Paid' : invoice.status === 'overdue' ? 'Overdue' : 'Pending'}
                    </div>
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <div class="no-invoices">No invoices yet</div>
          {/if}
        </div>
      {:else}
        <div class="billing-tab-panel" role="tabpanel" aria-label="Payment methods">
          {#if isLoadingCards}
            <div class="cards-loading">
              <i class="spinner loading icon" aria-hidden="true"></i>
              <span>Loading payment methods...</span>
            </div>
          {:else if savedCards.length > 0}
            <div class="cards-list">
              {#each savedCards as card}
                <div class="card-item">
                  <div class="card-main">
                    <div class="card-icon">
                      <i class="{getCardBrandIcon(card.brand)} icon" aria-hidden="true"></i>
                    </div>
                    <div class="card-details">
                      <div class="card-brand-row">
                        <span class="card-brand">{capitalizeFirst(card.brand)}</span>
                        {#if card.is_default}
                          <span class="default-badge">Default</span>
                        {/if}
                      </div>
                      <div class="card-number">
                        <span class="card-dots">•••• •••• ••••</span>
                        <span class="card-last4">{card.last4}</span>
                      </div>
                    </div>
                  </div>
                  {#if !card.is_default}
                    <div class="card-actions">
                      <button
                        type="button"
                        class="card-action-btn set-default-btn"
                        disabled={isSettingDefault}
                        on:click|stopPropagation={() => openSetDefaultConfirm(card)}
                        aria-label="Set as default payment method"
                      >
                        Set as default
                      </button>
                      <button
                        type="button"
                        class="card-action-btn remove-btn"
                        disabled={isRemovingCard}
                        on:click|stopPropagation={() => openRemoveConfirm(card)}
                        aria-label="Remove this card"
                      >
                        Remove
                      </button>
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {:else}
            <div class="no-cards">
              <i class="credit card outline icon" aria-hidden="true"></i>
              <span>No payment method on file</span>
              <button type="button" class="add-card-link" on:click={() => showAddCardModal = true}>
                Add a payment method
              </button>
            </div>
          {/if}
        </div>
      {/if}
    </div>
    {/if}
</div>

<PackPurchaseModal bind:showModal={showPackModal} on:purchased={reloadAll} />
<AddonPurchaseModal bind:showModal={showAddonModal} addons={addonServices} on:purchased={handleAddonPurchased} />
<AddCardModal bind:showModal={showAddCardModal} on:added={loadCards} />
<ConfirmDialog
  bind:show={showRemoveConfirm}
  title="Remove card"
  message="Remove this payment method? You can add it again later."
  confirmText="Remove"
  cancelText="Cancel"
  type="danger"
  icon="trash alternate"
  on:confirm={doRemoveCard}
  on:cancel={closeRemoveConfirm}
/>
<ConfirmDialog
  show={showSetDefaultConfirm}
  title="Set as default"
  message="Use this card as your default payment method?"
  confirmText="Yes"
  cancelText="No"
  type="info"
  icon="credit card"
  on:confirm={doSetDefaultCard}
  on:cancel={closeSetDefaultConfirm}
/>

<style>
  .payment-plan-page {
    padding: var(--space-5) var(--space-6);
  }
  @media (max-width: 768px) { .payment-plan-page { padding: var(--space-4); } }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 1100px) {
    .info-grid {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }
  }

  .info-card {
    background: var(--white);
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .card-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--grey);
    margin: 0 0 1.25rem 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Skeleton loading styles */
  .skeleton-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .skeleton {
    background: linear-gradient(90deg, var(--greyish) 25%, #e8e8e8 50%, var(--greyish) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .skeleton-title {
    height: 1.5rem;
    width: 60%;
  }

  .skeleton-price {
    height: 2rem;
    width: 40%;
  }

  .skeleton-badge {
    height: 1.5rem;
    width: 35%;
    border-radius: 9999px;
  }

  .skeleton-button {
    height: 2.5rem;
    width: 100%;
    margin-top: 0.5rem;
  }

  .skeleton-usage {
    height: 2.5rem;
    width: 50%;
  }

  .skeleton-progress {
    height: 8px;
    width: 100%;
  }

  .skeleton-text {
    height: 1rem;
    width: 70%;
  }

  .skeleton-invoice {
    height: 4rem;
    width: 100%;
    margin-bottom: 0.75rem;
  }

  .invoices-loading {
    padding: 0.5rem 0;
  }

  .plan-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .plan-usage-block {
    padding-top: var(--space-4);
    border-top: 1px solid var(--border-color);
    margin-top: var(--space-1);
  }

  .plan-usage-label {
    margin: 0 0 var(--space-3);
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--grey-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .plan-card-combined .usage-info {
    gap: var(--space-3);
  }

  .plan-card-combined .usage-main {
    flex-wrap: wrap;
  }

  .plan-card-combined .usage-current {
    font-size: 1.75rem;
  }

  .plan-name-large {
    font-size: 1.5rem;
    font-weight: 500;
    color: var(--grey);
  }

  .plan-price-large {
    font-size: 1.75rem;
    font-weight: 500;
    color: var(--grey);
  }

  .price-period {
    font-size: 1rem;
    color: var(--grey-secondary);
    margin-left: 0.25rem;
    font-weight: 400;
  }

  .trial-info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3);
    background: var(--status-success-soft);
    border-radius: var(--radius-input);
    margin-top: var(--space-1);
  }

  .trial-expiry {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--status-success-ink);
  }

  .trial-expiry i {
    font-size: 1rem;
    margin: 0;
  }

  .trial-expiry-date {
    font-size: 0.75rem;
    color: #15803d;
    padding-left: 1.5rem;
  }

  .trial-usage-message {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    background: #ecfdf5;
    border-radius: 6px;
    color: #16a34a;
    font-size: 0.875rem;
  }

  .trial-usage-message i {
    font-size: 1.25rem;
  }

  .next-payment {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--greyish);
  }

  .next-payment-label {
    font-size: 0.75rem;
    color: var(--grey-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .next-payment-date {
    font-size: 1rem;
    font-weight: 500;
    color: var(--grey);
  }

  .change-plan-button {
    width: 100%;
    padding: 0.75rem 1.5rem;
    background: transparent;
    border: 1px solid var(--primary);
    color: var(--primary);
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
    margin-top: 0.5rem;
  }

  .change-plan-button:hover {
    background: var(--primary);
    color: var(--white);
  }

  .usage-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .free-tier-message {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    background: #e7f3ff;
    border-radius: 6px;
    color: var(--primary);
    font-size: 0.875rem;
  }

  .free-tier-message i {
    font-size: 1.25rem;
  }

  .usage-main {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }

  .usage-current {
    font-size: 2rem;
    font-weight: 500;
    color: var(--grey);
  }

  .usage-separator {
    font-size: 1.5rem;
    color: var(--grey-secondary);
  }

  .usage-total {
    font-size: 1.5rem;
    font-weight: 500;
    color: var(--grey-secondary);
  }

  .usage-label-text {
    font-size: 0.875rem;
    color: var(--grey-secondary);
    margin-left: 0.5rem;
  }

  .usage-progress {
    width: 100%;
    height: 8px;
    background: var(--greyish);
    border-radius: 4px;
    overflow: hidden;
  }

  .usage-progress-bar {
    height: 100%;
    background: var(--secondary);
    transition: width 0.3s;
  }

  .usage-progress-bar.warning {
    background: #ffc107;
  }

  .usage-progress-bar.full {
    background: #dc3545;
  }

  .usage-remaining {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    margin-top: var(--space-2);
  }

  .renewal-hint {
    display: block;
    font-size: var(--text-xs);
    margin-top: var(--space-1);
  }

  .addon-empty {
    margin: 0;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  .addon-subtitle {
    margin: 0 0 var(--space-2) 0;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
  }

  .addon-purchases {
    margin-bottom: var(--space-4);
  }

  .addon-purchase-row {
    display: flex;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-2) 0;
    border-bottom: 1px solid var(--border-color);
    font-size: var(--text-sm);
    color: var(--grey);
  }

  .addon-purchase-meta {
    color: var(--grey-secondary);
    font-variant-numeric: tabular-nums;
  }

  .credits-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .credits-main {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .credits-available {
    font-size: 2rem;
    font-weight: 500;
    color: var(--primary);
  }

  .credits-label {
    font-size: 0.875rem;
    color: var(--grey-secondary);
  }

  .packs-summary {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--greyish);
  }

  .pack-summary-item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    font-size: 0.875rem;
  }

  .pack-name {
    color: var(--grey);
  }

  .pack-briefs {
    font-weight: 500;
    color: var(--primary);
  }

  .no-packs {
    font-size: 0.875rem;
    color: var(--grey-secondary);
    font-style: italic;
  }

  .purchase-pack-button {
    width: 100%;
    padding: 0.75rem 1.5rem;
    background: #45c496;
    color: var(--white);
    border: 0;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
    transition: background-color 0.2s;
    margin-top: 0.5rem;
  }

  .purchase-pack-button:hover {
    background: #3aaa82;
  }

  /* Billing tabs (invoices + payment methods) */
  .billing-tabs-section {
    background: var(--white);
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
  }

  .billing-tabs-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: var(--space-4);
    margin-bottom: var(--space-4);
  }

  .billing-tabs-nav {
    flex: 1;
    min-width: 0;
  }

  .billing-tabs-nav :global(.tab-bar) {
    margin-bottom: 0;
  }

  .billing-tab-actions {
    flex-shrink: 0;
    padding-bottom: var(--space-2);
  }

  .billing-tab-panel {
    min-height: 6rem;
  }

  .cards-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .card-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    background: var(--greyish);
    border-radius: 6px;
  }

  .card-main {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;
  }

  .card-brand-row {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .default-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #45c496;
    color: var(--white);
  }

  .card-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .card-action-btn {
    padding: 0.4rem 0.75rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    border: 1px solid var(--border-color);
    background: var(--white);
    color: var(--grey);
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
  }

  .card-action-btn:hover:not(:disabled) {
    background: var(--greyish);
    border-color: var(--grey-secondary);
  }

  .card-action-btn.set-default-btn {
    border-color: var(--primary);
    color: var(--primary);
  }

  .card-action-btn.set-default-btn:hover:not(:disabled) {
    background: rgba(5, 108, 242, 0.08);
  }

  .card-action-btn.remove-btn {
    border-color: #d91b42;
    color: #d91b42;
  }

  .card-action-btn.remove-btn:hover:not(:disabled) {
    background: rgba(217, 27, 66, 0.08);
  }

  .card-action-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .card-icon {
    font-size: 2rem;
    color: var(--grey);
    width: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-icon i {
    margin: 0;
  }

  .card-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .card-brand {
    font-weight: 500;
    color: var(--grey);
    font-size: 1rem;
  }

  .card-number {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--grey-secondary);
    font-family: monospace;
  }

  .card-dots {
    letter-spacing: 1px;
  }

  .card-last4 {
    font-weight: 500;
    color: var(--grey);
  }

  .card-expiry {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
  }

  .expiry-label {
    font-size: 0.75rem;
    color: var(--grey-secondary);
    text-transform: uppercase;
  }

  .expiry-date {
    font-weight: 500;
    color: var(--grey);
  }

  .no-cards {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: var(--grey-secondary);
    text-align: center;
    gap: 0.5rem;
  }

  .no-cards i {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }

  .cards-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem;
    color: var(--grey-secondary);
  }

  .cards-loading i {
    font-size: 1.25rem;
  }

  .add-card-button {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    background: transparent;
    border: 1px solid var(--primary);
    color: var(--primary);
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
  }

  .add-card-button i {
    margin: 0;
    font-size: 0.75rem;
  }

  .add-card-button:hover {
    background: var(--primary);
    color: var(--white);
  }

  .add-card-link {
    margin-top: 0.75rem;
    padding: 0.5rem 1rem;
    background: transparent;
    border: 1px solid var(--primary);
    color: var(--primary);
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
  }

  .add-card-link:hover {
    background: var(--primary);
    color: var(--white);
  }

  /* Invoices list */
  .view-all-button {
    padding: 0.5rem 1rem;
    background: transparent;
    border: 1px solid var(--primary);
    color: var(--primary);
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.875rem;
    transition: background-color 0.2s, color 0.2s;
  }

  .view-all-button:hover {
    background: var(--primary);
    color: var(--white);
  }

  .invoices-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .invoice-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: var(--greyish);
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .invoice-item:hover {
    background: #e9ecef;
  }

  .invoice-item:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .invoice-main {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .invoice-number {
    font-weight: 500;
    color: var(--grey);
  }

  .invoice-date {
    font-size: 0.875rem;
    color: var(--grey-secondary);
  }

  .invoice-details {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .invoice-amount {
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--grey);
  }

  .invoice-status {
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    background: #fff3cd;
    color: #856404;
  }

  .invoice-status.paid {
    background: #d4edda;
    color: #155724;
  }

  .invoice-status.overdue {
    background: #f8d7da;
    color: #721c24;
  }

  .no-invoices {
    text-align: center;
    padding: 2rem;
    color: var(--grey-secondary);
    font-style: italic;
  }

  @media (max-width: 768px) {
    .payment-plan-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .billing-tabs-toolbar {
      flex-direction: column;
      align-items: stretch;
      gap: var(--space-3);
    }

    .billing-tab-actions {
      padding-bottom: 0;
    }

    .billing-tab-actions .view-all-button,
    .billing-tab-actions .add-card-button {
      width: 100%;
    }

    .view-all-button {
      width: 100%;
    }

    .card-item {
      flex-wrap: wrap;
    }

    .card-expiry {
      width: 100%;
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid rgba(0, 0, 0, 0.05);
    }
  }
</style>
